import type { DoctorShift } from '../../types/doctorShift';
import type {
  DoctorShiftRepository,
  UpdateDoctorShiftParams,
  OperationResult,
} from './ports/DoctorShiftRepository';
import { validateShift, isPastDate } from '../../domain/doctorShift/ShiftValidationPolicy';

/**
 * 医師シフト更新ユースケース
 *
 * @description
 * 既存の医師シフトを更新する。
 * 楽観的ロックにより同時編集の競合を検出する。
 * 過去日付のシフトは編集不可。
 *
 * @param {DoctorShiftRepository} repository - リポジトリ実装
 * @param {UpdateDoctorShiftParams} params - 更新パラメータ
 * @returns {Promise<OperationResult<DoctorShift>>} 操作結果
 *
 * @example
 * const result = await updateDoctorShiftUseCase(repository, {
 *   id: 'shift-123',
 *   start_time: '10:00',
 *   end_time: '13:00',
 *   version: 1
 * });
 */
export const updateDoctorShiftUseCase = async (
  repository: DoctorShiftRepository,
  params: UpdateDoctorShiftParams
): Promise<OperationResult<DoctorShift>> => {
  // IDは必須
  if (!params.id) {
    return {
      success: false,
      error: 'シフトIDが指定されていません。',
      errorCode: 'VALIDATION_ERROR',
    };
  }

  // 更新対象フィールドがある場合のみバリデーション
  if (
    params.doctor_id ||
    params.shift_date ||
    params.start_time ||
    params.end_time
  ) {
    const validation = validateShift({
      doctor_id: params.doctor_id,
      shift_date: params.shift_date,
      start_time: params.start_time,
      end_time: params.end_time,
    });

    // 部分更新の場合、存在するフィールドのみバリデーション
    const relevantErrors = validation.errors.filter((error) => {
      if (error.includes('医師') && !params.doctor_id) return false;
      if (error.includes('日付') && !params.shift_date) return false;
      if (error.includes('開始時間') && !params.start_time) return false;
      if (error.includes('終了時間') && !params.end_time) return false;
      return true;
    });

    if (relevantErrors.length > 0) {
      return {
        success: false,
        error: relevantErrors.join('\n'),
        errorCode: 'VALIDATION_ERROR',
      };
    }
  }

  // 過去日付チェック（日付が更新される場合のみ）
  if (params.shift_date && isPastDate(params.shift_date)) {
    return {
      success: false,
      error: '過去の日付にはシフトを変更できません。',
      errorCode: 'VALIDATION_ERROR',
    };
  }

  return repository.updateShift(params);
};
